<template>
  <template v-if="isInPreviewMode && isEditable">
    <div class="overlay-piece" :style="overlayStyles.top" @click.stop>
      <iColumn
        wrap="nowrap"
        align="center"
        vertical-align="bottom"
        height="fill"
        class="preview-actions"
      >
        <iRow>
          <StagingToolbarPreviewActions
            display-size-actions
            display-theme-actions
            :theme-variant="themeVariant"
            :screen-variant="screenVariant"
            @change:theme-variant="changeThemeVariant"
            @change:screen-variant="changeScreenVariant"
          />
        </iRow>
      </iColumn>
    </div>
    <div class="overlay-piece show" :style="overlayStyles.bottom" />
    <div class="overlay-piece show" :style="overlayStyles.left" />
    <div class="overlay-piece show" :style="overlayStyles.right" />
    <div class="overlay-border" :style="overlayStyles.wrapper" />
  </template>
  <iColumn
    class="editor-content-wrapper"
    :width="myEditorContentWidth"
    position="relative"
    :style="{
      'z-index': !isPublishedAndLive ? actionsVisibleOnOverlayZIndex : 1,
    }"
    wrap="nowrap"
    align="center"
  >
    <iRow wrap="nowrap" class="story-actions-wrapper" :max-width="1200">
      <iRow :wrap="!$vssMobile ? 'nowrap' : 'wrap'" align="between">
        <iColumn gap="extraSmall">
          <iRow
            :wrap="!$vssMobile ? 'nowrap' : 'wrap'"
            :gap="shouldShowAssignToPublicationButton ? 'extraSmall' : 'small'"
            class="story-actions-container"
          >
            <iColumn vertical-align="middle" gap="extraSmall">
              <iRow
                vertical-align="middle"
                gap="extraSmall"
                wrap="nowrap"
                class="story-actions-left-container"
              >
                <iRow
                  gap="none"
                  vertical-align="middle"
                  width="hug"
                  wrap="nowrap"
                >
                  <CreateVariantDropdown
                    v-if="shouldShowAssignToPublicationButton"
                    :story-id="story.story_id"
                    overlay-position="top-left"
                    :z-index="actionsVisibleOnOverlayZIndex"
                    @assign="assignStory"
                  >
                    <template #trigger>
                      <iButton
                        size="extraSmall"
                        variant="tertiary"
                      >
                        <iRow
                          gap="small"
                          vertical-align="middle"
                          width="hug"
                          wrap="nowrap"
                        >
                          <iText variant="subtle">
                            Destination:
                          </iText>
                          <iText :wrap="false" :style-overrides="{textStyle: 'italic', textStyleHover: 'italic'}">
                            unassigned
                          </iText>
                        </iRow>
                      </iButton>
                    </template>
                  </CreateVariantDropdown>
                  <iButton
                    v-else
                    size="extraSmall"
                    variant="text"
                  >
                    <iRow
                      gap="small"
                      vertical-align="middle"
                      width="hug"
                      wrap="nowrap"
                    >
                      <iText variant="subtle">
                        Destination:
                      </iText>
                      <iText :wrap="false">
                        {{ story.publishing_platform_name }}
                      </iText>
                    </iRow>
                  </iButton>
                </iRow>
                <iColumn width="hug">
                  <StoryVariantDropdown
                    :parent-id="story.variant_parent_id"
                    :story-id="story.story_id"
                    :story-variant-count="story.variant_count"
                    :z-index="actionsVisibleOnOverlayZIndex"
                    :in-preview-mode="isInPreviewMode"
                  />
                </iColumn>
              </iRow>
              <iRow wrap="nowrap" class="story-flag-wrapper">
                <iIcon
                  v-if="canPublishStories && (story.publishing_platform_type === PUBLISHING_PLATFORM_TYPE.PUBLICATION)"
                  :name="isStoryExclusive ? 'mdi:dollar' : 'mdi:dollar-off'"
                  :is-loading="updateStoryExclusivityIsLoading"
                  :tooltip="isStoryExclusive ? 'Exclusive' : 'Free'"
                  class="exclusive-icon-toggle"
                  :label="isStoryExclusive ? 'Behind Paywall Only' : 'Paywall Optional'"
                  disable-hover-effect
                  variant="branded"
                  @click="updateStoryExclusivityFlag"
                />
                <iIcon
                  v-if="canPublishStories && isSyndicationMarketplace"
                  :is-loading="updateStoryExclusivityIsLoading"
                  :label="isStoryExclusive ? 'Behind Paywall Only' : 'Paywall Optional'"
                  :tooltip="exclusiveCheckboxTooltip"
                  variant="branded"
                  :name="isStoryExclusive ? 'mdi:dollar' : 'mdi:dollar-off'"
                  @click="updateStoryExclusivityFlag"
                />
                <iIcon
                  v-if="canPublishStories && isSyndicationMarketplace"
                  :is-loading="updateStoryRequiresSyndicationApprovalContentIsLoading"
                  variant="branded"
                  :name="story.requires_syndication_approval ? 'majesticons:lock-line' : 'majesticons:lock-off-line'"
                  :label="story.requires_syndication_approval ? 'Approval Required' : 'No Approval Necessary'"
                  tooltip="When enabled you will need to approve the syndication of this story."
                  @click="updateStoryRequiresSyndicationApprovalFlag"
                />
                <iIcon
                  v-if="canPublishStories && isSyndicationMarketplace"
                  :is-loading="updateStoryAllowRemixContentIsLoading"
                  variant="branded"
                  :name="story.allow_remix ? 'ph:pencil-simple-light' : 'ph:pencil-simple-slash-light'"
                  :label="story.allow_remix ? 'Remixing Allowed' : 'No Remixing'"
                  tooltip="When enabled you allow users to modify your story before syndicating it."
                  @click="updateStoryAllowRemixFlag"
                />
              </iRow>
            </iColumn>
            <iRow
              align="right"
              vertical-align="middle"
              wrap="nowrap"
            >
              <iText
                v-if="!isInPreviewMode"
                :wrap="false"
                variant="branded"
              >
                {{ savingStatus }}
              </iText>
              <iRow
                v-if="!isInPreviewMode"
                wrap="nowrap"
                gap="extraSmall"
                width="hug"
              >
                <iButton
                  v-if="isEditable"
                  ref="undoButton"
                  v-tooltip="'Undo'"
                  icon-left="undo"
                  :disabled="!canUndo"
                  size="extraSmall"
                  variant="tertiary"
                  @click="handleUndo"
                />
                <iButton
                  v-if="isEditable"
                  ref="redoButton"
                  v-tooltip="'Redo'"
                  icon-left="redo"
                  :disabled="!canRedo"
                  size="extraSmall"
                  variant="tertiary"
                  @click="handleRedo"
                />
              </iRow>
              <iText
                v-else
                :wrap="false"
                variant="success"
              >
                {{ publishingStatus }}
              </iText>
              <iButton
                v-if="isEditable"
                tooltip="Show preview mode"
                variant="secondary"
                size="small"
                @click="togglePreview"
              >
                {{ isInPreviewMode ? "Edit" : "Preview" }}
              </iButton>
              <iButton
                v-if="story.status === 'draft'"
                :is-loading="isPublishingStory"
                :loader-on-left="true"
                loading-spinner-position="before"
                size="small"
                @click="openShowPublishModal"
              >
                Publish
              </iButton>
              <iButton
                v-else-if="!isPreview"
                :is-loading="isUnpublishingStory"
                loading-spinner-position="before"
                size="small"
                variant="secondary"
                @click="unpublishStoryAction"
              >
                Edit
              </iButton>
            </iRow>
          </iRow>
          <iRow
            class="story-title-container"
            width="fill"
            align="between"
            vertical-align="middle"
            wrap="wrap"
          >
            <iRow :max-width="900">
              <iTextEditInput
                ref="title"
                v-model="title"
                :disabled="isInPreviewMode"
                :min-width="250"
                width="fill"
                auto-save-on="input"
                editable-on-click
                font-size="extraExtraLarge"
                font-weight="dark"
                hide-borders
                hide-cta-buttons
                inline
                placeholder="Untitled Story"
                @submit="saveTitleWithDebounce($event)"
              />
            </iRow>
            <iRow
              align="left"
              vertical-align="middle"
              wrap="nowrap"
            >
              <iRow
                class="story-hashtags-container"
                gap="none"
                vertical-align="middle"
                align="left"
                wrap="nowrap"
                width="fill"
              >
                <StoryHashtagInput
                  v-if="canEditStories"
                  v-model="hashtags"
                  :edit="!isInPreviewMode"
                  :validation="validateHashtag"
                  hashtag-value-key="hashtag"
                  placeholder="+Tags"
                  @submit:hashtag="addHashtag"
                  @delete:hashtag="removeHashtag"
                />
              </iRow>
              <iRow
                align="right"
                width="fill"
                vertical-align="middle"
                gap="extraSmall"
              >
                <iUploader
                  :acceptable-file-types="acceptableFileTypes"
                  @upload-file="handleThumbnailUpload"
                >
                  <iButton
                    variant="secondary"
                    size="extraSmall"
                    :is-loading="uploadStoryThumbnailLoading"
                  >
                    {{ thumbnailText }}
                  </iButton>
                </iUploader>
                <iButton
                  v-if="hasThumbnail"
                  variant="tertiary"
                  size="extraSmall"
                  :style-overrides="{
                    smallPaddingTop: 'extraSmall',
                    smallPaddingLeft: 'extraSmall',
                    smallPaddingRight: 'extraSmall',
                    smallPaddingBottom: 'extraSmall',
                  }"
                  @click="handleThumbnailRemove"
                >
                  <iIcon variant="secondary" name="mdi:trash-outline" />
                </iButton>
                <iDropdown
                  v-if="hasThumbnail"
                  :show-dropdown="showThumbnailPreview"
                  :teleport-to-body="false"
                  :z-index="3"
                >
                  <template #trigger>
                    <iButton
                      variant="tertiary"
                      size="extraSmall"
                      :style-overrides="{
                        smallPaddingTop: 'extraSmall',
                        smallPaddingLeft: 'extraSmall',
                        smallPaddingRight: 'extraSmall',
                        smallPaddingBottom: 'extraSmall',
                      }"
                      @click="showThumbnailPreview = true"
                    >
                      <iIcon variant="secondary" name="mdi:eye" />
                    </iButton>
                  </template>
                  <template #overlay>
                    <iColumn>
                      <iButton
                        class="fullscreen-button"
                        size="extraSmall"
                        variant="secondary"
                        @click.stop="viewFullscreen"
                      >
                        <iIcon name="material-symbols:fullscreen-rounded" />
                      </iButton>
                      <iButton
                        class="close-button"
                        size="extraSmall"
                        variant="secondary"
                        @click.stop="showThumbnailPreview = false"
                      >
                        <iIcon name="material-symbols:close-rounded" />
                      </iButton>
                      <iImageContainer id="thumb-image" :src="story.thumbnail" :width="300" />
                    </iColumn>
                  </template>
                </iDropdown>
              </iRow>
            </iRow>
          </iRow>
        </iColumn>
      </iRow>
    </iRow>
    <iRow
      class="story-editor-component-wrapper"
      align="center"
      :style-overrides="{
        paddingLeft: '40px',
        paddingRight: '40px'
      }"
      overflow="auto"
      :height="editorHeightOffset"
    >
      <iEditor
        ref="editor"
        :key="playbackVersion"
        :branding-styles="getCssMappedConfiguration"
        :generate-text="generateStoryTextContent"
        :generate-product-collection="generateProductCollectionFromAi"
        :generate-text-changes="generateStoryTextContentChanges"
        :generate-link-ranges-with-tracking-urls="generateLinkRangesWithTrackingUrls"
        :include-margin="includeMargin"
        :post-type="story.variant_type"
        :preview="isInPreviewMode"
        :preview-post-data="previewPostData"
        :preview-theme-id="previewThemeId"
        :search-products="searchProducts"
        :segments="resolvedSegments"
        :max-width="1200"
        use-border
        :upload-post-image="uploadImageFunction"
        :upload-post-video="uploadVideoFunction"
        :upload-post-audio="uploadAudioFunction"
        :pick-media="openMediaPicker"
        :on-add-audio-metadata="handleAddAudioMetadata"
        :min-height="900"
        height="auto"
        variant="builder"
        width="fill"
        :style-overrides="{
          backgroundColor: editorBackgroundColor,
          hoverBackgroundColor: editorBackgroundColor,
          builderBorderColor: !isInPreviewMode ? editorBorderColor : 'none'
        }"
        @input="saveWithDebounce"
        @handle-link-modal-open="handleLinkModalOpen"
      >
        <template #above-editor />
      </iEditor>
    </iRow>
  </iColumn>
  <template v-if="story">
    <CreateVariantsModal
      :show="showCreateVariantsModal"
      :story-id="story.story_id"
      :story-title="story.title"
      @close="showCreateVariantsModal = false"
    />
    <iModal
      v-if="showEditStoryConfirmation"
      :close-on-backdrop="false"
      :close-on-escape="false"
      :close-on-secondary="false"
      :max-width="600"
      :primary-action-button-loading="updateStoryIsLoading"
      primary-action-button-label="Unpublish"
      title="Edit Story"
      @close="closeEditStoryConfirmation"
      @click:primary="forceDraftStatusConfirmation"
      @click:secondary="closeEditStoryConfirmation"
    >
      <template #body>
        <iParagraph>
          Your story is currently live. To edit your story you need to unpublish it. This will remove your story from
          the platforms it is published on until you republish it.
        </iParagraph>
      </template>
    </iModal>
  </template>
  <StoryPublishModal
    v-if="showPublishModal"
    :is-submitting="isPublishingStory"
    :selected-date="storyDate"
    :selected-time="storyTime"
    @publish-now="publishNow"
    @schedule-publish="schedulePublish"
    @submit-review="submitForReview"
    @close="closePublishModal"
  />
  <iModal
    v-if="showNeedsDestinationToPublish"
    :close-on-backdrop="true"
    :close-on-escape="true"
    :close-on-secondary="true"
    :max-width="600"
    primary-action-button-label=""
    title="Publish Story"
    :visible="showNeedsDestinationToPublish"
    :show-primary-action-button="false"
    secondary-action-button-label="Ok"
    @close="closeEditStoryConfirmation"
    @click:secondary="showNeedsDestinationToPublish = false"
  >
    <template #body>
      <iText>
        You must select a destination before you can publish this story.
      </iText>
    </template>
  </iModal>
  <MediaPickerModal
    :visible="mediaPickerVisible"
    :media-type="mediaPickerType"
    :theme-id="0"
    @select="handleMediaPickerSelect"
    @close="handleMediaPickerClose"
  />
  <teleport to="body">
    <RichTextDestinationPickerModal
      :show="showLinkModal"
      :original-link-value="originalLinkData.linkValue"
      :link-data="linkData"
      :publication-id="story.publishing_platform_id"
      :rich-text-ref="linkData.ref"
      @close="handleLinkModalClose"
      @update="linkData = {...linkData, ...$event}"
    />
  </teleport>
</template>

<script>
// @TODO Edit Article Toolbar Center Controls contains the new code that should be used here - JN
import { mapActions, mapState, mapWritableState } from "pinia";
import { usePublicationStore } from "@/stores/publication-store";
import { useStyleStore, useWindowStore } from "@vcl";
import CreateVariantsModal from "@/components/publication/CreateVariantsModal";
import { useStoryStore } from "%/stores/story-store";
import { editorSurfaceColor } from "%/helpers/editor-surface-color";
import { STORY_VARIANT_TYPES } from "@/constants/story-variant-type-constants";
import { PUBLISHING_PLATFORM_TYPE } from "%/constants/story-constants";
import { useAffiliateLinkStore } from "%/stores/affiliate-link-store";
import { useGeneralStore } from "%/stores/general-store";
import StoryVariantDropdown from "@/components/StoryVariantDropdown.vue";
import useBrandingStore from "%/stores/branding-store";
import { camelToSnakeCase } from "@bloglovin/vue-framework";
import CreateVariantDropdown from "@/components/stories/CreateVariantDropdown.vue";
import StagingToolbarPreviewActions from "~/components/staging-frame/structure/StagingToolbarPreviewActions.vue";
import StoryHashtagInput from "%/components/post/StoryHashtagInput.vue";
import { useUndoRedoStore } from "%/stores/undo-redo-store";
import { uniqueId } from "lodash";
import RichTextDestinationPickerModal from "%/components/RichTextDestinationPickerModal.vue";
import { useCollaboratorStore } from "@/stores/collaborator-store";
import StoryPublishModal from "@/components/publication/StoryPublishModal.vue";
import MediaPickerModal from "%/components/MediaPickerModal.vue";
import { StoryVideoPlaybackMixin } from "%/mixins/story-video-playback-mixin";
import {
  STORY_PUBLISH_STATUS_ACTIVE,
  STORY_PUBLISH_STATUS_DRAFT,
  STORY_PUBLISH_STATUS_SCHEDULED,
  STORY_PUBLISH_STATUS_SUBMITTED_FOR_REVIEW,
  STORY_PUBLISH_STATUS_NEEDS_EDITING,
  STORY_PUBLISH_STATUS_SCHEDULED_NEEDS_REVIEW,
  STORY_STATUS_LABEL_ACTIVE,
  STORY_STATUS_LABEL_DRAFT,
  STORY_STATUS_LABEL_SCHEDULED,
  STORY_STATUS_LABEL_SUBMITTED_FOR_REVIEW,
  STORY_STATUS_LABEL_NEEDS_EDITING,
  STORY_STATUS_LABEL_SCHEDULED_NEEDS_REVIEW,
} from "~/constants/story-status-constants";
import dayjs from "dayjs";

export default {
  name: "StoryEditorComponentDark",
  components: {
    RichTextDestinationPickerModal,
    StagingToolbarPreviewActions,
    CreateVariantDropdown,
    StoryVariantDropdown,
    CreateVariantsModal,
    StoryHashtagInput,
    StoryPublishModal,
    MediaPickerModal,
  },
  mixins: [StoryVideoPlaybackMixin],
  props: {
    storyData: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    saveWhileActive: {
      type: Boolean,
      required: false,
      default: false,
    },
    previewMode: {
      type: Boolean,
      required: false,
      default: false,
    },
    includeMargin: {
      type: Boolean,
      required: false,
      default: true,
    },
    marginWidth: {
      type: Number,
      required: false,
      default: 200,
    },
  },
  data() {
    return {
      themeVariant: "light-mode",
      screenVariant: "desktop",
      isStoryExclusive: false,
      isStoryAllowRemix: false,
      isStoryRequiresSyndicationApproval: false,
      showEditStoryConfirmation: false,
      debounce: null,
      storyLastUpdated: null,
      title: "",
      isPreview: false,
      triggerHashtagsModal: false,
      showShowStoryVariantsModal: false,
      showCreateVariantsModal: false,
      showNeedsDestinationToPublish: false,
      initialLoad: 0,
      savingStatus: "Changes Saved",
      forceDraftStatus: false,
      STORY_VARIANT_TYPES,
      PUBLISHING_PLATFORM_TYPE,
      savingStatuses: {
        PREVIEW: "Displaying Preview",
        SAVED: "Changes Saved",
        UNSAVED: "Unsaved Changes",
        SAVING: "...Saving Changes",
        ERROR: "Error Saving Changes",
      },
      savingStoryContentStatuses: [
        "Unsaved Changes",
        "...Saving Changes",
      ],
      overlayStyles: {
        top: {},
        bottom: {},
        left: {},
        right: {},
        wrapper: {},
      },
      editorHeightOffset: 0,
      uniqueKey: uniqueId(),
      undoRedoJustClicked: false,
      showLinkModal: false,
      linkData: {},
      originalLinkData: {},
      showPublishModal: false,
      mediaPickerVisible: false,
      mediaPickerType: "image",
      pickMediaResolve: null,
      storyDate: null,
      storyTime: null,
      showThumbnailPreview: false,
    };
  },
  computed: {
    ...mapState(useWindowStore, [
      "$vssMobile",
    ]),
    ...mapState(usePublicationStore, [
      "publicationId",
      "myPublications",
    ]),
    ...mapState(useCollaboratorStore, [
      "role",
      "canPublishStories",
      "canEditStories",
    ]),
    ...mapState(useBrandingStore, [
      "getCssMappedConfiguration",
    ]),
    ...mapState(useStoryStore, [
      "uploadStoryThumbnailLoading",
      "uploadStoryImageLoading",
      "loadingAddHashtagToStory",
      "errorAddHashtagToStory",
      "updateStoryExclusivityIsLoading",
      "updateStoryRequiresSyndicationApprovalContentIsLoading",
      "updateStoryAllowRemixContentIsLoading",
      "isPublishingStory",
      "isUnpublishingStory",
    ]),
    ...mapState(useStoryStore, {
      storyStoreData: "story",
    }),
    ...mapWritableState(useStoryStore, [
      "updateStoryIsLoading",
      "assignToPublishingPlatformModalOpen",
      "forcePublishAfterAssigning",
    ]),
    ...mapState(useGeneralStore, ["userData", "username"]),
    ...mapState(useUndoRedoStore, [
      "redoQueue",
      "undoQueue",
    ]),
    acceptableFileTypes() {
      return [
        ".jpeg",
        ".jpg",
        ".png",
        ".gif",
        ".webp",
        ".bmp",
      ];
    },
    canUndo() {
      return !this.savingStoryContentStatuses.includes(this.savingStatus) &&
          (this.undoQueue[this.uniqueKey] || []).length > 0;
    },
    canRedo() {
      return !this.savingStoryContentStatuses.includes(this.savingStatus) &&
          (this.redoQueue[this.uniqueKey] || []).length > 0;
    },
    myEditorContentWidth() {
      if (!this.isInPreviewMode) {
        return "fill";
      }
      if (this.screenVariant === "tablet") {
        return 700;
      }
      if (this.screenVariant === "mobile") {
        return 400;
      }
      return "fill";
    },
    previewPostData() {
      return {
        authorName: this.username,
        authorHandler: camelToSnakeCase(this.username.replaceAll(" ", "")),
        authorAvatar: this.userData?.avatar_url,
      };
    },
    hasPendingChanges() {
      return [
        this.savingStatuses.UNSAVED,
        this.savingStatuses.SAVING,
        this.savingStatuses.ERROR,
      ].includes(this.savingStatus);
    },
    isInPreviewMode() {
      return (
        this.isPublishedAndLive ||
        this.isScheduled ||
        this.isSubmittedForReview ||
        this.isNeedsEditing ||
        this.isScheduledButNeedsReview ||
        this.isPreview ||
        this.previewMode
      );
    },
    isScheduled() {
      return this.story.status === STORY_PUBLISH_STATUS_SCHEDULED;
    },
    isSubmittedForReview() {
      return this.story.status === STORY_PUBLISH_STATUS_SUBMITTED_FOR_REVIEW;
    },
    isPublishedAndLive() {
      return this.story.status === STORY_PUBLISH_STATUS_ACTIVE && this.savingStatus === "Changes Saved" && !this.forceDraftStatus;
    },
    isNeedsEditing() {
      return this.story.status === STORY_PUBLISH_STATUS_NEEDS_EDITING;
    },
    isScheduledButNeedsReview() {
      return this.story.status === STORY_PUBLISH_STATUS_SCHEDULED_NEEDS_REVIEW;
    },
    publishingStatus() {
      switch (this.story.status) {
        case STORY_PUBLISH_STATUS_SCHEDULED:
          return STORY_STATUS_LABEL_SCHEDULED;

        case STORY_PUBLISH_STATUS_SUBMITTED_FOR_REVIEW:
          return STORY_STATUS_LABEL_SUBMITTED_FOR_REVIEW;

        case STORY_PUBLISH_STATUS_NEEDS_EDITING:
          return STORY_STATUS_LABEL_NEEDS_EDITING;

        case STORY_PUBLISH_STATUS_SCHEDULED_NEEDS_REVIEW:
          return STORY_STATUS_LABEL_SCHEDULED_NEEDS_REVIEW;

        case STORY_PUBLISH_STATUS_ACTIVE:
          return STORY_STATUS_LABEL_ACTIVE;

        case STORY_PUBLISH_STATUS_DRAFT:
          return STORY_STATUS_LABEL_DRAFT;

        default:
          return STORY_STATUS_LABEL_DRAFT;
      }
    },
    isSyndicationMarketplace() {
      return this.story.publishing_platform_type === PUBLISHING_PLATFORM_TYPE.SYNDICATION_MARKETPLACE;
    },
    shouldShowAssignToPublicationButton() {
      return this.story.publishing_platform_id === 0 && !this.isSyndicationMarketplace;
    },
    story() {
      if (this.storyData && typeof this.storyData === "object" && Object.keys(this.storyData).length === 0) {
        return this.storyStoreData;
      }
      return this.storyData;
    },
    // Segment source the playback mixin resolves media-backed videos against.
    playbackSegments() {
      if (!this.story || !this.story.segments) {
        return [];
      }
      return this.segments;
    },
    segments() {
      const segmentArray = Object.values(this.story.segments);
      return this.story.segment_order.reduce((acc, curr) => {
        const segment = segmentArray.find(seg => seg.story_segment_id === curr.story_segment_id);
        return [
          ...acc,
          segment,
        ];
      }, []);
    },
    previewThemeId() {
      if (this.story.variant_type === "x_post") {
        return -1;
      }
      return 0;
    },
    exclusiveCheckboxTooltip() {
      return "You can mark this content as exclusive. This will make it only visible to active subscribers.";
    },
    themeBackgroundColor() {
      return this.paperColor();
    },
    themeDarkBorderColor() {
      return this.darkBorderColor();
    },
    editorBackgroundColor() {
      return editorSurfaceColor(this.themeBackgroundColor, "paper");
    },
    editorBorderColor() {
      return editorSurfaceColor(this.themeDarkBorderColor, "dark");
    },
    hashtags: {
      get() {
        return this.story.hashtags || [];
      },
      set(value) {
        this.story.hashtags = value;
      },
    },
    hashTagInputLeftOffset() {
      if (!this.hashtags.length) {
        return "-9px";
      }
      return 0;
    },
    topStoryActionsLeftOffset() {
      if (this.shouldShowAssignToPublicationButton) {
        return "-9px";
      }
      return 0;
    },
    overlayZIndex() {
      return 1;
    },
    actionsVisibleOnOverlayZIndex() {
      return 1;
    },
    isEditable() {
      return (
        !this.isPublishedAndLive &&
        !this.isScheduled &&
        !this.isSubmittedForReview &&
        !this.isNeedsEditing &&
        !this.isScheduledButNeedsReview
      );
    },
    hasThumbnail() {
      return this.story.thumbnail && this.story.thumbnail.length > 0;
    },
    thumbnailText() {
      if (!this.hasThumbnail) {
        return "Upload Thumbnail";
      }

      return "Update Thumbnail";
    },
  },
  watch: {
    "story.publishing_platform_id": {
      immediate: true,
      handler(value) {
        if (this.story.publishing_platform_type === "publication") {
          window.blclient.data.links.fanWeb = window.blclient.data.links.web + `/destination/${value}`;
        }
      },
    },
  },
  mounted() {
    this.getStoryDateAndTime();
    if (this.story.title) {
      this.title = this.story.title || "New Draft Story";
    }
    this.isStoryExclusive = !!this.story.is_exclusive;
    this.isStoryAllowRemix = !!this.story.allow_remix;
    this.isStoryRequiresSyndicationApproval = !!this.story.requires_syndication_approval;
    useWindowStore().init();
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("beforeunload", this.handleBeforeUnload);
    window.addEventListener("resize", this.calculateOverlay);
    let urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("preview") && urlParams.get("preview") === "true") {
      this.togglePreview();
    }
    this.calculateAvailableSpaceForEditor();
  },
  beforeUnmount() {
    useWindowStore().remove();
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
    window.removeEventListener("resize", this.calculateOverlay);
  },
  methods: {
    calculateAvailableSpaceForEditor() {
      const storyActionsWrapperDom = document.querySelector(".story-actions-wrapper");
      if (storyActionsWrapperDom) {
        const actionsWrapperBottom = storyActionsWrapperDom.getBoundingClientRect().bottom + 12;
        const windowHeight = window.innerHeight;
        this.editorHeightOffset = windowHeight - actionsWrapperBottom - 12;
      }
    },
    ...mapActions(useBrandingStore, [
      "paperColor",
      "darkBorderColor",
    ]),
    ...mapActions(useStoryStore, [
      "uploadStoryImage",
      "uploadStoryVideo",
      "uploadStoryAudio",
      "uploadStoryThumbnail",
      "removeStoryThumbnail",
      "updateStory",
      "addHashtagsToStory",
      "removeHashtagFromStory",
      "updateStoryExclusiveContentFlag",
      "updateStoryAllowRemixContentFlag",
      "updateStoryRequiresSyndicationApprovalContentFlag",
      "updateStoryTitle",
      "assignStoryToPublishingPlatform",
      "publishingStory",
      "triggerStoryAssignModal",
      "unpublishStory",
      "schedulePublishingStory",
      "submitForReviewStory",
      "schedulePublishingStoryNeedsReview",
    ]),
    ...mapActions(useAffiliateLinkStore, [
      "searchAffiliateLinkProducts",
      "createLink",
    ]),
    ...mapActions(useUndoRedoStore, [
      "undo",
      "redo",
      "addToRedoQueue",
      "addToUndoQueue",
    ]),
    handleLinkModalOpen(event) {
      if (this.story.publishing_platform_type === "publication") {
        this.linkData = event;
        this.originalLinkData = event;
        this.showLinkModal = true;
        return;
      }
      event.openModal(true);
    },
    handleLinkModalClose() {
      this.linkData = {};
      this.originalLinkData = {};
      this.showLinkModal = false;
    },
    async generateLinkRangesWithTrackingUrls(ranges, callback) {
      const promises = ranges.map(range => {
        return new Promise(resolve => {
          this.createLink(
            range.url,
            result => {
              if (result.short_link) {
                resolve({
                  ...range,
                  trackingUrl: result.short_link,
                  shortLinkData: result,
                });
              } else {
                resolve(range);
              }
            },
            () => {
              resolve(range);
            },
          );
        });
      });

      const results = await Promise.all(promises);
      callback(results);
    },
    checkAndHandleUndoKeyDown(event) {
      if (!event.shiftKey && (event.ctrlKey || event.metaKey)) {
        if (event.key === "z" && this.canUndo) {
          event.preventDefault();
          this.$refs.undoButton.$el.click();
        }
      }
    },
    checkAndHandleRedoKeyDown(event) {
      if (event.shiftKey && (event.ctrlKey || event.metaKey)) {
        if (event.key === "z" && this.canRedo) {
          event.preventDefault();
          this.$refs.redoButton.$el.click();
        }
      }
    },
    onKeyDown(event) {
      this.checkAndHandleUndoKeyDown(event);
      this.checkAndHandleRedoKeyDown(event);
    },
    togglePreview() {
      this.isPreview = !this.isPreview;
      if (this.isPreview) {
        this.$nextTick(() => {
          this.calculateOverlay();
        });
        this.savingStatus = this.savingStatuses.PREVIEW;
      } else {
        this.resetOverlay();
        this.savingStatus = this.savingStatuses.SAVED;
      }
    },
    handleBeforeUnload(event) {
      if (this.hasPendingChanges) {
        event.preventDefault();
        event.returnValue = "";
      }
    },
    closeEditStoryConfirmation() {
      this.showEditStoryConfirmation = false;
    },
    forceDraftStatusConfirmation() {
      this.showEditStoryConfirmation = false;
      this.forceDraftStatus = true;
      this.saveTitleWithDebounce(this.title);
    },
    handleUndo() {
      this.undoRedoJustClicked = true;
      sessionStorage.setItem("story-undo-redo", "true");
      const previousPayload = this.undo(this.uniqueKey);
      this.addToRedoQueue(
        this.uniqueKey,
        {
          segments: this.story.segments,
          order: this.story.segment_order,
        },
      );
      this.story.segments = previousPayload.segments;
      this.story.segment_order = previousPayload.order;
    },
    handleRedo() {
      this.undoRedoJustClicked = true;
      sessionStorage.setItem("story-undo-redo", "true");
      const previousPayload = this.redo(this.uniqueKey);
      this.addToUndoQueue(
        this.uniqueKey,
        {
          segments: this.story.segments,
          order: this.story.segment_order,
        },
      );
      this.story.segments = previousPayload.segments;
      this.story.segment_order = previousPayload.order;
    },
    handleBeforeUpdate() {
      if (this.undoRedoJustClicked) {
        this.undoRedoJustClicked = false;
        return;
      }
      this.addToUndoQueue(
        this.uniqueKey,
        {
          segments: this.story.segments,
          order: this.story.segment_order,
        },
      );
    },
    saveWithDebounce(content) {
      this.handleBeforeSave(() => {
        if (this.debounce) {
          clearTimeout(this.debounce);
        }
        this.debounce = setTimeout(() => {
          this.savingStatus = this.savingStatuses.SAVING;
          this.handleBeforeUpdate();
          // Keep { mediaId } canonical — never persist the display-only resolved src.
          this.submitStory({ ...content, segments: this.stripResolvedVideoSrc(content.segments || []) }, () => {
            this.setSavingStatusToSaved();
          }, () => {
            this.savingStatus = this.savingStatuses.ERROR;
          });
        }, 1000);
      });
    },
    saveTitleWithDebounce($event) {
      this.handleBeforeSave(() => {
        this.title = $event;
        this.calculateAvailableSpaceForEditor();
        this.updateStoryIsLoading = true;
        if (this.debounce) {
          clearTimeout(this.debounce);
        }
        this.debounce = setTimeout(() => {
          this.savingStatus = this.savingStatuses.SAVING;
          this.updateStoryTitle(this.story.story_id, this.title, () => {
            this.setSavingStatusToSaved();
          }, () => {
            this.savingStatus = this.savingStatuses.ERROR;
          });
        }, 1000);
      });
    },
    addHashtag(newTag) {
      this.savingStatus = this.savingStatuses.SAVING;
      this.addHashtagsToStory([newTag], this.story.story_id, () => {
        this.savingStatus = this.savingStatuses.ERROR;
        this.hashtags.pop();
        this.calculateAvailableSpaceForEditor();
      }, () => {
        this.setSavingStatusToSaved();
      });
    },
    handleBeforeSave(process = () => {
    }) {
      if (this.isPublishedAndLive) {
        return;
      }
      this.savingStatus = this.savingStatuses.UNSAVED;
      process();
    },
    removeHashtag({ el, index }) {
      this.savingStatus = this.savingStatuses.SAVING;
      this.removeHashtagFromStory(el.hashtag_id, this.story.story_id, () => {
        this.savingStatus = this.savingStatuses.ERROR;
        this.hashtags.splice(index, 0, el);
        this.calculateAvailableSpaceForEditor();
      }, () => {
        this.setSavingStatusToSaved();
      });
    },
    validateHashtag(value) {
      const result = /^(#)?[a-zA-Z0-9_]+$/.test(value);
      if (!result) {
        return "Hashtag must be alphanumeric and can only contain underscores.";
      }
      return true;
    },
    setSavingStatusToSaved() {
      setTimeout(() => {
        this.savingStatus = this.savingStatuses.SAVED;
      }, 200);
    },
    uploadImageFunction(file) {
      return this.uploadStoryImage(file);
    },
    uploadVideoFunction(file) {
      return this.uploadStoryVideo(file);
    },
    uploadAudioFunction(file) {
      return this.uploadStoryAudio(file);
    },
    // Audio segment "Add metadata" → pick a cover image via the universal media
    // picker. Stored as a media_id; StoryVideoPlaybackMixin resolves it to a
    // playback URL (injected as the transient coverImageSrc) at render.
    async handleAddAudioMetadata({ segmentId }) {
      const media = await this.openMediaPicker("image");
      if (media && media.mediaId) {
        this.$refs.editor.updateSegmentData(segmentId, { coverImage: media.mediaId });
      }
    },
    // Passed to iEditor as :pick-media. A media segment calls it instead of the
    // native file dialog; resolves to { mediaId } once the user picks (or null
    // on cancel). The segment stores the media_id; StoryVideoPlaybackMixin
    // resolves it to a playback URL at render.
    openMediaPicker(mediaType) {
      return new Promise(resolve => {
        this.mediaPickerType = mediaType;
        this.mediaPickerVisible = true;
        this.pickMediaResolve = resolve;
      });
    },
    handleMediaPickerSelect(media) {
      this.mediaPickerVisible = false;
      if (!this.pickMediaResolve) {
        return;
      }
      this.pickMediaResolve(media && media.media_id ? { mediaId: media.media_id } : null);
      this.pickMediaResolve = null;
    },
    handleMediaPickerClose() {
      this.mediaPickerVisible = false;
      if (!this.pickMediaResolve) {
        return;
      }
      this.pickMediaResolve(null);
      this.pickMediaResolve = null;
    },
    async searchProducts(query) {
      return await this.searchAffiliateLinkProducts(query);
    },
    async generateStoryTextContent(prompt, successCallback) {
      return await useStoryStore().generateTextStoryContent(prompt, successCallback);
    },
    async generateProductCollectionFromAi(prompt, successCallback, errorCallback) {
      return await useStoryStore().generateAiProductCollection(prompt, successCallback, errorCallback);
    },
    async generateStoryTextContentChanges(prompt, content, successCallback) {
      return await useStoryStore().generateTextStoryContentChanges(prompt, content, successCallback);
    },
    submitStory(content, onComplete = () => {
    }, onError = () => {
    }) {
      if (this.story.status !== STORY_PUBLISH_STATUS_ACTIVE) {
        this.updateStoryIsLoading = false;
      }
      this.updateStory(this.story.story_id, content, onComplete, onError);
    },
    updateStoryExclusivityFlag() {
      this.savingStatus = this.savingStatuses.SAVING;
      this.updateStoryExclusiveContentFlag(this.story.story_id, !this.isStoryExclusive, () => {
        this.setSavingStatusToSaved();
        this.isStoryExclusive = !this.isStoryExclusive;
      }, () => {
        this.savingStatus = this.savingStatuses.ERROR;
      });
    },
    updateStoryAllowRemixFlag() {
      this.savingStatus = this.savingStatuses.SAVING;
      this.updateStoryAllowRemixContentFlag(this.story.story_id, !this.isStoryAllowRemix, () => {
        this.setSavingStatusToSaved();
        this.isStoryAllowRemix = !this.isStoryAllowRemix;
      }, () => {
        this.savingStatus = this.savingStatuses.ERROR;
      });
    },
    updateStoryRequiresSyndicationApprovalFlag() {
      this.savingStatus = this.savingStatuses.SAVING;
      this.updateStoryRequiresSyndicationApprovalContentFlag(this.story.story_id,
        !this.isStoryRequiresSyndicationApproval,
        () => {
          this.setSavingStatusToSaved();
          this.isStoryRequiresSyndicationApproval = !this.isStoryRequiresSyndicationApproval;
        },
        () => {
          this.savingStatus = this.savingStatuses.ERROR;
        });
    },
    assignStory(details) {
      this.savingStatus = this.savingStatuses.SAVING;
      this.assignToPublishingPlatformModalOpen = false;

      this.assignStoryToPublishingPlatform(
        this.story.story_id,
        details.publishing_platform_type,
        details.publishing_platform_id,
        "",
        () => {
          this.setSavingStatusToSaved();
          this.story.publishing_platform_id = details.publishing_platform_id;
          this.story.publishing_platform_type = details.publishing_platform_type;
          this.story.publishing_platform_name = details.publishing_platform_name;
          if (details.publishing_platform_type === "publication") {
            useBrandingStore().getThemeForPublication(details.publishing_platform_id,
              ({
                themeId,
                themePalette,
                semanticPalette,
                theme,
                brandingConfig,
              }) => {
                useBrandingStore().publicationThemeId = themeId;
                useBrandingStore().configuration = brandingConfig;
                useStyleStore().setThemeData(
                  themeId,
                  theme,
                  themePalette,
                  semanticPalette,
                  useStyleStore().DEFAULT_APP_ID,
                );
              });
          }
          if (this.forcePublishAfterAssigning) {
            this.publishingStory();
            this.forcePublishAfterAssigning = false;
          }
        },
        () => {
          this.savingStatus = this.savingStatuses.ERROR;
        },
      );
    },
    async unpublishStoryAction() {
      if ((this.story.status === STORY_PUBLISH_STATUS_ACTIVE ||
          this.isScheduled ||
          this.isSubmittedForReview
      ) && !this.saveWhileActive && !this.forceDraftStatus) {
        this.showEditStoryConfirmation = true;
        return;
      }
      this.unpublishStory(this.story.story_id, true);
    },
    resetOverlay() {
      this.overlayStyles = {
        top: {},
        bottom: {},
        left: {},
        right: {},
        wrapper: {},
      };
    },
    calculateOverlay() {
      this.calculateAvailableSpaceForEditor();
      if (!this.isInPreviewMode) {
        this.resetOverlay();
      }
      const storyActionsWrapper = document.querySelector(".story-actions-wrapper");
      const actionsContainer = document.querySelector(".story-actions-container");
      if (!actionsContainer || !storyActionsWrapper) {
        return;
      }
      const actionsContainerRectangle = actionsContainer.getBoundingClientRect();
      const storyActionsWrapperRectangle = storyActionsWrapper.getBoundingClientRect();

      this.overlayStyles = {
        top: {
          top: "0px",
          left: `${storyActionsWrapperRectangle.left - 24}px`,
          width: `${storyActionsWrapperRectangle.width + 44}px`,
          height: `${actionsContainerRectangle.top - 16}px`,
          zIndex: 999999999,
        },
        bottom: {
          height: "14px",
          left: `${storyActionsWrapperRectangle.left - 24}px`,
          width: `${storyActionsWrapperRectangle.width + 44}px`,
          bottom: "0px",
          zIndex: 999999999,
        },
        left: {
          top: "0px",
          left: "0px",
          width: `${storyActionsWrapperRectangle.left - 24}px`,
          height: "100vh",
          zIndex: 999999999,
        },
        right: {
          top: "0px",
          left: `${storyActionsWrapperRectangle.right + 20}px`,
          right: "0px",
          height: "100vh",
          zIndex: 999999999,
        },
        wrapper: {
          top: `${actionsContainerRectangle.top - 13}px`,
          left: `${storyActionsWrapperRectangle.left - 21}px`,
          width: `${storyActionsWrapperRectangle.width + 38}px`,
          bottom: "17px",
        },
      };
    },
    changeThemeVariant(themeVariant) {
      this.themeVariant = themeVariant;
    },
    changeScreenVariant(screenVariant) {
      this.screenVariant = screenVariant;
      this.$nextTick(() => {
        this.calculateOverlay();
      });
    },
    getUserRole() {
      return useCollaboratorStore().role;
    },
    openShowPublishModal() {
      this.showPublishModal = true;
    },
    closePublishModal() {
      this.showPublishModal = false;
    },
    publishNow() {
      const shouldShowButton = this.shouldShowAssignToPublicationButton;
      if (shouldShowButton) {
        this.showNeedsDestinationToPublish = true;
      } else {
        this.publishingStory();
      }
    },
    schedulePublish(timestamp) {
      const shouldShowButton = this.shouldShowAssignToPublicationButton;
      if (shouldShowButton) {
        this.showNeedsDestinationToPublish = true;
      } else {
        if (this.canPublishStories) {
          this.schedulePublishingStory(timestamp);
        } else if (this.canEditStories) {
          this.schedulePublishingStoryNeedsReview(timestamp);
        }
      }
    },
    submitForReview() {
      const shouldShowButton = this.shouldShowAssignToPublicationButton;
      if (shouldShowButton) {
        this.showNeedsDestinationToPublish = true;
      } else {
        this.submitForReviewStory();
      }
    },
    handleThumbnailUpload(file) {
      this.uploadStoryThumbnail(this.story.story_id, file);
    },
    handleThumbnailRemove() {
      this.removeStoryThumbnail(this.story.story_id);
    },
    viewFullscreen() {
      const imageObject = document.getElementById("thumb-image");
      if (imageObject.requestFullscreen) {
        imageObject.requestFullscreen();
      } else if (imageObject.mozRequestFullScreen) {
        imageObject.mozRequestFullScreen();
      } else if (imageObject.webkitRequestFullscreen) {
        imageObject.webkitRequestFullscreen();
      } else if (imageObject.msRequestFullscreen) {
        imageObject.msRequestFullscreen();
      }
    },
    getStoryDateAndTime() {
      const timestamp = this.story.timestamp_scheduled;

      if (!timestamp) {
        this.storyDate = null;
        this.storyTime = null;
        return;
      }

      const dateObj = dayjs.unix(timestamp);
      this.storyDate = dateObj.format("YYYY-MM-DD");
      this.storyTime = dateObj.format("HH:mm");
    },
  },
  styleGuide: () => ({
    "previewBackgroundColor": { "color.background": "dark" },
    "previewActionsPaddingBottom": { "size.spacing": "standard" },
    "previewActionsPaddingInline": { "size.spacing": "large" },
    "storyActionsBackgroundColor": { "color.background": "paper" },
  }),
};
</script>


<style lang="scss" scoped>
:deep(.story-actions-wrapper.row) {
  padding-inline: v-bind("$getStyles.previewActionsPaddingInline");
}

:deep(.editor-wrapper.row) {
  margin-bottom: 16px;
}

:deep(.story-editor-component-wrapper.row) {
  position: relative;
  bottom: 5px;
  padding-top: 5px;
}

.story-actions-left-container {
  position: relative;
  left: v-bind(topStoryActionsLeftOffset);
}

.overlay-border {
  z-index: v-bind(overlayZIndex);
  position: fixed;
  background-color: transparent;
  outline: 2px solid white;
  border-radius: 5px;
}

.overlay-piece {
  position: fixed;
  background: v-bind("$getStyles.previewBackgroundColor");
  z-index: v-bind(overlayZIndex);

  opacity: 0;
  animation: fadeIn 0.5s ease-in forwards;
}

@keyframes fadeIn {
  to {
    opacity: 1;
  }
}

.overlay-piece.fade-out {
  opacity: 0;
}

.preview-actions {
  padding-bottom: v-bind("$getStyles.previewActionsPaddingBottom");
}

.editor-content-wrapper {
  margin: 0 auto;
}

.overlay-piece {
  :deep(svg) {
    position: relative;
    top: 2px;
    fill: white;
  }

  :deep(.i-button--variant-secondary) {
    svg {
      fill: black;
    }
  }
}

.story-flag-wrapper {
  position: relative;
  right: 4px;
  gap: 16px;
}

.close-button {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 4;
}

.fullscreen-button {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 4;
}

</style>
